/**
 * Stage 6.5 Part 2: Frontend Telemetry Service
 * Tracks dashboard interactions (no PII)
 */

import API_URL from '../config/api';
import { supabase } from './supabase';

interface TelemetryEvent {
  eventType: string;
  eventData: Record<string, any>;
}

class TelemetryService {
  private eventQueue: TelemetryEvent[] = [];
  private flushInterval: number | null = null;
  private readonly FLUSH_INTERVAL_MS = 30000; // 30 seconds
  private readonly MAX_BATCH_SIZE = 50;

  constructor() {
    this.startFlushInterval();
    this.setupBeforeUnload();
  }

  /**
   * Track a telemetry event
   */
  track(eventType: string, eventData: Record<string, any> = {}) {
    // Remove any PII
    const sanitizedData = this.sanitizeData(eventData);
    
    this.eventQueue.push({
      eventType,
      eventData: sanitizedData,
    });

    // Auto-flush if queue is full
    if (this.eventQueue.length >= this.MAX_BATCH_SIZE) {
      this.flush();
    }
  }

  /**
   * Track dashboard render
   */
  trackDashboardRender(loadTimeMs: number, cardCount: number, interestsCount: number) {
    this.track('dashboard_render', {
      loadTimeMs,
      cardCount,
      interestsCount,
    });
  }

  /**
   * Track card hover
   */
  trackCardHover(cardType: string, confidence?: number) {
    this.track('card_hover', {
      cardType,
      confidence,
    });
  }

  /**
   * Track card click
   */
  trackCardClick(cardType: string, actionType: string) {
    this.track('card_click', {
      cardType,
      actionType,
    });
  }

  /**
   * Track shift viewed
   */
  trackShiftViewed(shiftType: string, confidence?: number) {
    this.track('shift_viewed', {
      shiftType,
      confidence,
    });
  }

  /**
   * Track shift dismissed
   */
  trackShiftDismissed(shiftType: string) {
    this.track('shift_dismissed', {
      shiftType,
    });
  }

  /**
   * Track shift rated
   */
  trackShiftRated(shiftType: string, rating: number) {
    this.track('shift_rated', {
      shiftType,
      rating,
    });
  }

  /**
   * Flush events to backend
   */
  async flush() {
    if (this.eventQueue.length === 0) return;

    const events = [...this.eventQueue];
    this.eventQueue = [];

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        // Store offline for later
        this.storeOffline(events);
        return;
      }

      const baseUrl = API_URL.replace('/v1', '') || 'http://localhost:4028';
      const response = await fetch(`${baseUrl}/v1/telemetry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ events }),
      });

      if (!response.ok) {
        // Store offline if request fails
        this.storeOffline(events);
      }
    } catch (error) {
      console.error('Failed to send telemetry', error);
      this.storeOffline(events);
    }
  }

  /**
   * Store events offline in IndexedDB
   */
  private async storeOffline(events: TelemetryEvent[]) {
    try {
      // Simple localStorage fallback (IndexedDB would be better for production)
      const stored = localStorage.getItem('telemetry_queue');
      const queue = stored ? JSON.parse(stored) : [];
      queue.push(...events);
      localStorage.setItem('telemetry_queue', JSON.stringify(queue.slice(-100))); // Keep last 100
    } catch (error) {
      console.error('Failed to store telemetry offline', error);
    }
  }

  /**
   * Start periodic flush interval
   */
  private startFlushInterval() {
    this.flushInterval = window.setInterval(() => {
      this.flush();
    }, this.FLUSH_INTERVAL_MS);
  }

  /**
   * Setup beforeunload handler to flush on page unload
   */
  private setupBeforeUnload() {
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  /**
   * Sanitize data to remove PII
   */
  private sanitizeData(data: Record<string, any>): Record<string, any> {
    const sanitized = { ...data };
    
    // Remove common PII fields
    const piiFields = ['email', 'name', 'username', 'phone', 'address', 'userId', 'user_id'];
    piiFields.forEach(field => {
      delete sanitized[field];
    });

    // Remove nested PII
    Object.keys(sanitized).forEach(key => {
      if (typeof sanitized[key] === 'object' && sanitized[key] !== null) {
        sanitized[key] = this.sanitizeData(sanitized[key]);
      }
    });

    return sanitized;
  }

  /**
   * Cleanup
   */
  destroy() {
    if (this.flushInterval) {
      clearInterval(this.flushInterval);
    }
  }
}

// Singleton instance
export const telemetry = new TelemetryService();

